// Deterministic building placement. Same seed + same MapParams → identical
// list on client and server, so visuals and collision exactly agree.

import { TerrainSampler } from "../game/Noise";
import { TERRAIN, type MapParams } from "./protocol";

export interface Building {
  x: number;     // world center
  z: number;
  yBase: number; // ground level under the building
  w: number;     // width  (X)
  d: number;     // depth  (Z)
  h: number;     // height (Y)
  rot: number;   // yaw radians (cosmetic; collision uses AABB w/d)
  kind: 0 | 1;   // 0 = small house, 1 = skyscraper
}

// Mulberry32 PRNG (reused from Noise.ts) seeded per cell so output is
// position-deterministic without a global state.
function cellRng(cx: number, cz: number, seed: number): () => number {
  let a = (seed ^ (cx * 0x1F1F1F1F) ^ (cz * 0x9E3779B1)) >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function generateBuildings(seed: number, sampler: TerrainSampler, params: MapParams): Building[] {
  const out: Building[] = [];
  const size = TERRAIN.size;
  const half = size / 2;
  const step = params.buildingGridStep;
  const sea = params.seaLevel;
  const isMetropolis = params.collidableBuildings;

  for (let cx = -half + step / 2; cx < half; cx += step) {
    for (let cz = -half + step / 2; cz < half; cz += step) {
      const r = cellRng(Math.floor(cx / step), Math.floor(cz / step), seed);
      if (r() > params.buildingDensity) continue;

      // Jitter position within the cell (less for metropolis = grid feel)
      const jitter = isMetropolis ? step * 0.10 : step * 0.30;
      const ox = cx + (r() - 0.5) * 2 * jitter;
      const oz = cz + (r() - 0.5) * 2 * jitter;

      // Sample terrain height under candidate building
      const groundH = sampleHeight(sampler, ox, oz, params);
      if (groundH < sea + 0.5) continue;
      // Skip on big slopes (rough filter)
      const dh = Math.abs(sampleHeight(sampler, ox + 6, oz, params) - groundH)
               + Math.abs(sampleHeight(sampler, ox, oz + 6, params) - groundH);
      if (dh > 6 && !isMetropolis) continue;

      const h = params.buildingHeightMin + r() * (params.buildingHeightMax - params.buildingHeightMin);
      const w = isMetropolis ? 8 + r() * 12 : 4 + r() * 4;
      const d = isMetropolis ? 8 + r() * 12 : 4 + r() * 4;
      const rot = isMetropolis ? 0 : r() * Math.PI * 2;
      out.push({
        x: ox, z: oz, yBase: groundH,
        w, d, h, rot,
        kind: isMetropolis ? 1 : 0,
      });
    }
  }
  return out;
}

// Match the height computation used by World.ts so building bases sit on terrain.
export function sampleHeight(sampler: TerrainSampler, x: number, z: number, params: MapParams): number {
  const raw = sampler.height(x, z, TERRAIN.maxHeight);
  // Apply per-map shape (matches World.applyMapShape)
  const seaShift = params.seaLevel;
  let h = raw * params.heightAmp;
  // Flatten toward sea level
  if (params.flatten > 0) h = h * (1 - params.flatten) + seaShift * params.flatten;
  return h;
}
